import { BankrateFees } from '@bankrate/mortgage-types'
import querystring from 'query-string'
import generateUniqueId from 'utils/generateUniqueId'
import { rollbar } from 'utils/rollbar'
import { useStoreState } from 'store'
import { generateClickDate } from 'utils/generateClickDate'
import { getReqFees } from 'utils/getReqFees'
import { BankrateDisplayOffer } from 'utils/offers'

const getFees = (fees: BankrateFees[]): number => {
  if (!fees || fees.length === 0) {
    return 0
  }
  return fees.map(fee => fee.amount).reduce((t, n) => t + n)
}

const buildCParam = (initialCParams: any, rowPosition: any, clickType: any, pid: any) => {
  return initialCParams
    .split(',')
    .map((item: any, index: number) => {
      if (index === 0) {
        return rowPosition || item
      }

      if (index === 1) {
        return clickType || item
      }

      if (index === 2) {
        return '0'
      }

      if (index === 5) {
        return pid || item
      }

      return item
    })
    .join(',')
}

export const useTrackingLink = (): any => {
  const { paidOffersCount, uniquePaidAdvertisersCount, showingStacked, stackedCriterias } = useStoreState(
    state => state.offers
  )
  const { showStackedOffers } = useStoreState(state => state.featureFlags.flags)
  const activeDisplayTab = useStoreState(state => state.dimensions.activeDisplayTab)
  const { ttcid, pid, searchChanged } = useStoreState(state => state.dimensions.dimensions)

  return {
    generateLink(
      offer: BankrateDisplayOffer,
      rowPosition: any,
      clickType: 8 | 11,
      ctr: string,
      purchaseId: string
    ): string | undefined {
      // This value is used as the formId in the lead form's tracking and should match the correlationId
      // we send in the ProductClicked event.
      const pur_id = purchaseId || generateUniqueId()
      try {
        // url returned by PROD gateway will always be PROD ep url
        // qa.bankrate.com points to PROD gateway but we don't want to use prod ep links on qa.bankrate.com
        // so update to qa if not in prod
        let epLink = offer.tracking?.link || ''
        if (process.env.REACT_APP_ENV !== 'production') {
          epLink = epLink.replace('ep.bankrate.com', 'ep.qa.bankrate.com')
        }

        const [path, params] = epLink.split('?')
        /*
         * Initial parameters of the tracking link from the Gateway are:
         * 'e', 'reqid', 'adsetid', 'lead', pc', 'c', 'cs', and sometimes 'isWholesale'
         *
         * Since we will override cs, first we remove them and get the rest
         */
        const initialParams = querystring.parse(params)
        const mrtParams: Record<string, string | number | boolean> = {
          // overrides
          c: buildCParam(initialParams.c, rowPosition, clickType, pid),
          cs: searchChanged ? 1 : 0,

          // additional rate MRT specific params
          click_date: generateClickDate(),
          ref_url: window.top!.location.href.split('#')[0],
          list: 'rate_table',
          featuredOffer: offer.isFeatured ? 1 : 0,
          fiveycol: offer.fiveYearCost || 0,
          eightycol: offer.eightYearCost || 0,
          totalfees: offer.upFrontCosts || 0,
          reqfees: getReqFees(offer),
          br_f: getFees(offer.fees),
          points: offer.product.points,
          pur_id: pur_id,
          sectionalPosition: rowPosition,
          ul: uniquePaidAdvertisersCount,
          pl: paidOffersCount,
          is_jumbo: offer.product.size ? offer.product.size === 'jumbo' : false, // if size is not set, assume not jumbo
          ttcid: ttcid || '',
          ctr: ctr,
        }

        // stacked offers
        if (showStackedOffers) {
          if (!stackedCriterias.enoughOffers) {
            mrtParams.location = 'rate-table'
          } else if (showingStacked) {
            mrtParams.location = offer.locationOverride === 'rate-table-stacked' ? 'stacked' : 'all'
          } else {
            mrtParams.location = 'all'
          }
        }
        // tabbed offers
        else {
          mrtParams.tab = activeDisplayTab === 'featured' ? 'f' : 'a'
        }

        let mergedParams = { ...initialParams, ...mrtParams }

        // clean up null|undefined values
        if (!mergedParams.ttcid) {
          delete mergedParams.ttcid
        }

        return `${path}?${querystring.stringify(mergedParams)}`
      } catch (err: any) {
        rollbar.error('Error mutating EP link on rate table', ...err)
      }
    },
  }
}
